import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import React, { useRef } from 'react';

import ContentContainer from '../components/Common/ContentContainer';
import { ArrowLink, PrimaryLink } from '../components/Common/Links';
import PageSection from '../components/Common/PageSection';
import RichTextConverter from '../components/Common/RichText/RichTextConverter';
import ScrollTo, { smoothScrollToRef } from '../components/Common/ScrollTo';
import Accordion from '../components/Generic/Accordion';
import Layout from '../components/Layouts/Layout';
import MarketingSeo from '../components/MarketingSeo';
import Customization from '../components/Plans/Customization';
import FeatureTable from '../components/Plans/FeatureTable';
import ShortFeatureList from '../components/Plans/ShortFeatureList';
import * as styles from './plansv2.module.css';

/*eslint-disable*/
const PlansV2 = ({ data: { page }, location }) => {
  const faqs = page.faqSection.content.map((f) => {
    return {
      title: f.title,
      supportingText: f.body,
    };
  });

  const featureTiers = page.shortFeatureList.tiers.map((t) => t.key);
  const scrollRef = useRef();
  const onScrollCta = (e) => {
    e.preventDefault();
    smoothScrollToRef(scrollRef, null, 84);
  };

  return (
    <Layout background={'grey'} pageMeta={page.pageMetaData}>
      <MarketingSeo meta={page.pageMetaData} slug={location.pathname} />
      <PageSection slim isTopSection className={styles.headingWrapper}>
        <div className={styles.heading}>
          <h1>
            {page.heading}
            {page.headingLine2 && (
              <>
                <br />
                {page.headingLine2}
              </>
            )}
          </h1>
          <RichTextConverter className={styles.lede} copy={page.headingParagraph} />
        </div>
      </PageSection>
      <PageSection style={{ paddingTop: 0 }}>
        <ShortFeatureList
          listHeading={page.shortFeatureList.heading}
          list={page.shortFeatureList}
          featureTiers={featureTiers}
          onScrollCta={onScrollCta}
        />
      </PageSection>

      <PageSection slim className={styles.darkBlue}>
        <Customization
          heading={page.customizeSectionHeading}
          mainCard={page.mainCustomizationCard}
          cards={page.customizationCards}
        />
      </PageSection>
      <PageSection slim className={styles.white}>
        <div className={styles.cta}>
          <h4>{page.freeCallToAction.title}</h4>
          <p>{page.freeCallToAction.description?.description}</p>
          <PrimaryLink label={page.freeCallToAction.ctaLabel} url={page.freeCallToAction.url} isButton />
        </div>
      </PageSection>
      <ScrollTo reference={scrollRef} />
      <PageSection slim className={styles.darkGradient}>
        <FeatureTable sections={page.featureList.sections} tiers={featureTiers} heading={page.featureList.heading} />
      </PageSection>
      {page.studySectionCopy && (
        <ContentContainer
          className={styles.study}
          style={{
            '--backgroundImg': `url(${page.studySectionBackground?.file?.url})`,
            '--mobileBackgroundImg': `url(${page.studySectionMobileBackground?.file?.url})`,
          }}
        >
          <div className={styles.studyCopyContainer}>
            <div className={styles.studyCopy}>
              <h3>{page.studySectionCopy.title}</h3>
              <RichTextConverter copy={page.studySectionCopy.body} />
              <ArrowLink label={page.studySectionCopy.linkLabel} url={page.studySectionCopy.linkUrl} />
              <GatsbyImage
                className={styles.copyImage}
                alt={page.studySectionCopy.image.title}
                image={page.studySectionCopy.image.gatsbyImageData}
              />
            </div>
          </div>
          <div />
        </ContentContainer>
      )}

      <PageSection slim className={styles.white}>
        <h3>{page.faqSection.title}</h3>
        <Accordion defaultCollapsed={true} items={faqs} />
      </PageSection>
      <PageSection slim>
        <div className={styles.footer}>
          <h3>{page.footerCallToAction.title}</h3>
          <div>
            <PrimaryLink label={page.footerCallToAction.ctaLabel} url={page.footerCallToAction.url} isButton />
          </div>
        </div>
      </PageSection>
    </Layout>
  );
};

export default PlansV2;

export const query = graphql`
  query PlansPage($slug: String!) {
    page: contentfulPlansPage(slug: { eq: $slug }) {
      heading
      headingLine2
      headingParagraph {
        references {
          contentful_id
          id
          linkText
          url
          opensNewPage
          isJumpLink
          internal {
            type
          }
        }
        raw
      }
      pageMetaData {
        ...PageMetaData
      }
      shortFeatureList {
        tiers {
          key
          title
          secondaryCtaUrl
          secondaryCtaLabel
          lede
          footerCtaUrl
          footerCtaLabel
          ctaUrl
          ctaLabel
        }

        features {
          heading
          list {
            tiers
            title
            tooltip
            footnote
          }
        }
      }
      customizeSectionHeading
      mainCustomizationCard {
        ...SimpleCard
      }
      customizationCards {
        ...SimpleCard
      }
      freeCallToAction {
        ...CallToAction
      }
      featureList {
        heading
        sections {
          title
          features {
            tiers
            title
            tooltip
            footnote
          }
        }
      }
      studySectionCopy {
        ...SimpleCard
      }
      studySectionBackground {
        gatsbyImageData
        file {
          url
        }
      }
      studySectionMobileBackground {
        gatsbyImageData
        file {
          url
        }
      }
      faqSection {
        title
        content {
          ... on ContentfulSimpleCard {
            ...SimpleCard
          }
        }
      }
      footerCallToAction {
        ...CallToAction
      }
    }
  }
`;

const featureList = {
  tiers: [
    {
      key: 'enterprise',
      title: 'Enterprise',
      lede: 'For large, cross-functional teams who need total clarity into digital portfolios.',
      ctaLabel: 'Get a demo',
      ctaUrl: '/demo',
      secondaryCtaLabel: '',
      secondaryCtaUrl: '',
      footerCtaLabel: 'View full comparison',
      footerCtaUrl: '#full-list',
    },
    {
      key: 'business',
      title: 'Business',
      lede:
        'For growth-minded teams who need actionable data to discover trends, perfect experiences, and plan roadmaps.',
      ctaLabel: 'Get a demo',
      ctaUrl: '/demo',
      secondaryCtaLabel: 'Or start a free 14-day trial',
      secondaryCtaUrl: '/signup',
      footerCtaLabel: 'View full comparison',
      footerCtaUrl: '#full-list',
    },
  ],

  features: {
    heading: 'Key capabilities',
    list: [
      {
        title: '100% tagless autocapture, with retroactive data history',
        footnote: '',
        tiers: ['enterprise', 'business'],
      },
      {
        title: 'Pixel-perfect session replay that excludes private and sensitive data by default',
        tiers: ['enterprise', 'business'],
      },
      {
        title: 'Actionable product analytics—funnel analysis, frustration signals, and more',
        tiers: ['enterprise', 'business'],
      },
      {
        title: 'Customizable replay retention',
        footnote: '(Starting at 1 month)',
        tiers: ['enterprise', 'business'],
      },
      {
        title: 'Customizable analytics data retention',
        footnote: '(Starting at 1 year)',
        tiers: ['enterprise', 'business'],
      },
      {
        title: 'Intelligent journey mapping and conversion analysis',
        tiers: ['enterprise'],
      },
      {
        title: 'Robust organization management and data export capabilities',
        tiers: ['enterprise'],
      },
    ],
  },
};

const allFeatures = {
  heading: 'Compare all features',
  sections: [
    {
      title: 'DX Data Engine',
      features: [
        {
          title: '100% tagless autocapture',
          tooltip: '100% tagless autocapture',
          tiers: ['business', 'enterprise'],
        },
        {
          title: 'Private and sensitive data excluded by default',
          tooltip: 'Private and sensitive data excluded by default',
          tiers: ['business', 'enterprise'],
        },
      ],
    },
    {
      title: 'Product Analytics',
      features: [
        {
          title: 'Frustration Signals',
          tooltip: 'Frustration Signals',
          tiers: ['business', 'enterprise'],
        },
        {
          title: 'Conversions analysis',
          tooltip: 'Frustration Signals',
          tiers: ['enterprise'],
        },
      ],
    },
    {
      title: 'Session Insights',
      features: [
        {
          title: 'Pixel-perfect session recreation',
          tooltip: 'Pixel-perfect session recreation',
          tiers: ['business', 'enterprise'],
        },
        {
          title: 'Heatmaps',
          tooltip: 'Heatmaps',
          tiers: ['enterprise'],
        },
      ],
    },
  ],
};
